import React from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Paper,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsMobileClassroom } from '../../store/selectors/studentSelectors';
import StatusChip from './StatusChip';
import QuestionNavigator from './QuestionNavigator';

interface Question {
  id: number;
  text: string;
  options: string[];
  correctAnswer: number;
  userAnswer?: number;
  isMarkedForReview?: boolean;
  isVisited?: boolean;
}

interface TestSidebarProps {
  questions: Question[];
  currentQuestionIndex: number;
  onQuestionSelect: (index: number) => void;
  onSubmitTest: () => void;
  sessionId: string;  // Changed from testId
  testTitle?: string;  // Add this
  testSubject?: string;  // Add this
  timeRemaining?: number;  // Add this
  totalTime?: number;  // Add this
}

const TestSidebar: React.FC<TestSidebarProps> = ({
  questions,
  currentQuestionIndex,
  onQuestionSelect,
  onSubmitTest,
  sessionId,  // Changed from testId
  testTitle,
  testSubject,
  timeRemaining,
  totalTime
}) => {
  const navigate = useNavigate();
  const isMobileClassroom = useSelector(selectIsMobileClassroom);
  
  // Use a default color since we removed selectedTest
  const testColor = '#1976d2';
  
  const answeredCount = questions.filter(q => q.userAnswer !== undefined).length;
  const submitDisabled = isMobileClassroom && answeredCount < 24;
  const markedCount = questions.filter(q => q.isMarkedForReview).length;
  const visitedCount = questions.filter(q => q.isVisited && q.userAnswer === undefined && !q.isMarkedForReview).length;
  const unattemptedCount = questions.length - answeredCount - markedCount - visitedCount;
  const progressPercentage = (answeredCount / questions.length) * 100;

  const handleBackToInstructions = () => {
    if (sessionId) {
      navigate('/start-test');
    }
  };

  // Helper function to lighten a color for hover effects
  const lightenColor = (color: string, amount: number = 0.1) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const newR = Math.min(255, Math.floor(r + (255 - r) * amount));
    const newG = Math.min(255, Math.floor(g + (255 - g) * amount));
    const newB = Math.min(255, Math.floor(b + (255 - b) * amount));
    
    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  return (
    <Paper 
      sx={{ 
        p: 0,
        height: '100%',
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        pb: 2,
        background: `linear-gradient(135deg, ${testColor} 0%, ${lightenColor(testColor, -0.2)} 100%)`,
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Test Progress
          </Typography>
        </Box>
      </Box>

      {/* Main Content Area - Flexible */}
      <Box sx={{ 
        pt: 2,
        pr: 1,
        pb: 3,
        pl: 1, // Reduced from 3 to 1.8 (40% reduction: 3 - 1.2 = 1.8)
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Status Summary */}
        <Box sx={{ 
          display: 'flex', 
          gap: 0.5, 
          mb: 2, 
          flexWrap: 'wrap',
          width: '100%' // Ensure full width
        }}>
          <StatusChip count={answeredCount} label="Done" bgcolor="#e8f5e8" color="#2e7d32" />
          <StatusChip count={markedCount} label="Marked" bgcolor="#fff3e0" color="#f57c00" />
          <StatusChip count={visitedCount} label="Visited" bgcolor="#f8e5ff" color="#8e24aa" />
          <StatusChip count={unattemptedCount} label="Pending" bgcolor="#ffffff" color="#616161" disabled />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Question Navigator - Now extracted */}
        <QuestionNavigator
          questions={questions}
          currentQuestionIndex={currentQuestionIndex}
          onQuestionSelect={onQuestionSelect}
          lightenColor={lightenColor}
        />

        {/* Progress Section - Moved below question palette */}
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{ 
              mb: 1, 
              height: 8, 
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#4caf50'
              }
            }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            {answeredCount} of {questions.length} questions completed
          </Typography>
        </Box>

        {/* Spacer to push submit button to bottom */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Action Buttons - Always at bottom with container for asymmetric positioning */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 3,
            p: 2,
            bgcolor: '#f5f5f5',
            borderRadius: 1,
            // Adjust container padding to shift buttons
            pl: 1, // Reduced left padding to shift "Resume Later" more left
            pr: 1  // Reduced right padding to shift "Submit Test" more right
          }}
        >
          <Button
            onClick={handleBackToInstructions}
            className="test-button-primary"
            size="medium"
            variant="contained"
            disabled={isMobileClassroom}
          >
            Resume Later
          </Button>
          
          <Button
            variant="contained"
            size="medium"
            onClick={onSubmitTest}
            className="test-button-primary"
            disabled={submitDisabled}
            title={submitDisabled ? `Answer at least 24 questions to submit (${answeredCount}/24)` : ''}
          >
            Submit Test
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default TestSidebar;